import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isTourMode, tourSample } from 'tour/tourMode';
import { GLOBAL_SEARCH_ENTITY_TYPES, GLOBAL_SEARCH_MAX_RECENT_IDS, GlobalSearchEntityType } from 'types/globalSearch';
import { resolveRecordFromPath } from './globalSearchRoutes';
import { getLocalStorage, removeLocalStorage, setLocalStorage } from './localStorage';

/**
 * The records this browser opened recently, most recent first.
 *
 * Per device by design: the list lives in `localStorage`, there is no server-side view table, and it
 * is read for the search dropdown's empty state, Home's Continue section, and the `recentIds` ranking
 * hint sent with every query. Nothing here is authoritative, so a lost or malformed list costs a hint,
 * never a result.
 *
 * The key is not scoped to a user, which is why {@link clearRecentlyViewed} is wired into every
 * logout: on a shared device the next person would otherwise be shown the previous one's case and
 * client names.
 */
const STORAGE_KEY = 'crespect.recently-viewed';

/** Kept at what the backend reads as a hint — a longer list would be stored and then ignored. */
export const RECENTLY_VIEWED_LIMIT = GLOBAL_SEARCH_MAX_RECENT_IDS;

export interface RecentlyViewedRecord {
    entityType: GlobalSearchEntityType;
    id: number;
    /** The record's name as its detail page showed it, so the empty state needs no fetch. */
    displayName: string;
    /** Epoch millis of the last time this record was opened. */
    viewedAt: number;
}

const KNOWN_ENTITY_TYPES = new Set<string>(GLOBAL_SEARCH_ENTITY_TYPES);

function toRecord(value: unknown): RecentlyViewedRecord | null {
    if (typeof value !== 'object' || value === null) {
        return null;
    }
    const { entityType, id, displayName, viewedAt } = value as Partial<RecentlyViewedRecord>;
    if (
        typeof entityType !== 'string' ||
        !KNOWN_ENTITY_TYPES.has(entityType) ||
        typeof id !== 'number' ||
        !Number.isSafeInteger(id) ||
        typeof displayName !== 'string' ||
        typeof viewedAt !== 'number' ||
        !Number.isFinite(viewedAt)
    ) {
        return null;
    }
    return { entityType: entityType as GlobalSearchEntityType, id, displayName, viewedAt };
}

/** Most recent first. An unreadable or malformed list reads as an empty one; the guided tour reads its own. */
export function readRecentlyViewed(): RecentlyViewedRecord[] {
    const sample = tourSample();
    if (sample) {
        return sample.recentlyViewed;
    }
    const raw = getLocalStorage(STORAGE_KEY);
    if (!raw) {
        return [];
    }

    try {
        const parsed: unknown = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
            return [];
        }
        return parsed
            .map(toRecord)
            .filter((record): record is RecentlyViewedRecord => record !== null)
            .slice(0, RECENTLY_VIEWED_LIMIT);
    } catch {
        // Malformed entry: treat as absent rather than breaking the bar.
        return [];
    }
}

/** Moves the record to the front, replacing any earlier visit to it, and drops the oldest overflow. */
export function recordRecentlyViewed(record: Omit<RecentlyViewedRecord, 'viewedAt'>): void {
    if (isTourMode()) {
        return;
    }
    const kept = readRecentlyViewed().filter(
        (existing) => existing.entityType !== record.entityType || existing.id !== record.id
    );
    const next = [{ ...record, viewedAt: Date.now() }, ...kept].slice(0, RECENTLY_VIEWED_LIMIT);
    setLocalStorage(STORAGE_KEY, JSON.stringify(next));
}

export function clearRecentlyViewed(): void {
    removeLocalStorage(STORAGE_KEY);
}

/**
 * The ranking hint for a search: `ENTITY_TYPE:id` per record. Ids are per-family, so a bare id would
 * boost whatever else shares the number in another family.
 */
export function recentIdsHint(): string[] {
    return readRecentlyViewed().map((record) => `${record.entityType}:${record.id}`);
}

/**
 * Records the page's own subject, if it has one.
 *
 * Mounted once, in `PageTitle`, rather than called from each detail page: every page already routes
 * its title through there, and a per-page call is a list that goes stale the first time a page is
 * added. The URL says which record is being read and the title says what it is called.
 */
export function useRecordRecentlyViewed(displayName: string | undefined): void {
    const { pathname } = useLocation();

    useEffect(() => {
        if (!displayName) {
            return;
        }
        const record = resolveRecordFromPath(pathname);
        if (record) {
            recordRecentlyViewed({ ...record, displayName });
        }
    }, [pathname, displayName]);
}
